import { useEffect, useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import ScrollToTop from '@/components/base/ScrollToTop';
import SEOHead from '@/components/feature/SEOHead';
import Breadcrumb from '@/components/feature/Breadcrumb';
import VideoCard from '@/components/feature/VideoCard';
import { videos, articles, categories, type VideoItem } from '@/mocks/videos';

export default function ArticlePage() {
  const { id } = useParams<{ id: string }>();
  const [video, setVideo] = useState<VideoItem | null>(null);
  const [article, setArticle] = useState<typeof articles[0] | null>(null);
  const [relatedVideos, setRelatedVideos] = useState<VideoItem[]>([]);

  useEffect(() => {
    window.scrollTo({ top: 0, behavior: 'smooth' });

    const videoId = parseInt(id || '1', 10);
    const foundVideo = videos.find(v => v.id === videoId) || videos[0];
    const foundArticle = articles.find(a => a.videoId === videoId) || articles[0];
    const related = videos
      .filter(v => v.category === foundVideo.category && v.id !== foundVideo.id)
      .slice(0, 6);

    setVideo(foundVideo);
    setArticle(foundArticle);
    setRelatedVideos(related);
  }, [id]);

  if (!video || !article) {
    return (
      <div className="min-h-screen bg-background-50 flex items-center justify-center">
        <div className="text-center">
          <i className="ri-loader-4-line text-4xl text-primary-400 animate-spin"></i>
          <p className="text-foreground-400 mt-4">加载中...</p>
        </div>
      </div>
    );
  }

  const categoryName = categories.find(c => c.slug === video.category)?.name || '分类';
  const articleJsonLd = {
    '@context': 'https://schema.org',
    '@type': 'Article',
    headline: article.title,
    description: article.summary,
    author: {
      '@type': 'Person',
      name: article.author,
    },
    datePublished: article.date,
    image: article.image,
    publisher: {
      '@type': 'Organization',
      name: '棒棒TV',
    },
  };

  const videoJsonLd = {
    '@context': 'https://schema.org',
    '@type': 'VideoObject',
    name: video.title,
    description: video.description,
    thumbnailUrl: video.cover,
    uploadDate: '2024-01-01',
    contentRating: video.rating.toString(),
    genre: video.tags.join(', '),
  };

  return (
    <>
      <SEOHead
        title={article.title}
        description={article.summary}
        keywords={`${video.title},${video.tags.join(',')},${video.category},棒棒TV,在线观看,免费观看,高清视频`}
        canonicalUrl={`/article/${video.id}`}
        ogType="article"
        ogImage={article.image}
        schemaJson={{
          '@context': 'https://schema.org',
          '@graph': [articleJsonLd, videoJsonLd],
        }}
      />
      <ScrollToTop />
      <div className="min-h-screen bg-background-50">
        <Navbar />
        <main className="pt-16">
          <div className="max-w-[1200px] mx-auto px-4 md:px-6 py-6">
            {/* Breadcrumb */}
            <Breadcrumb
              items={[
                { label: '首页', href: '/' },
                { label: categoryName, href: `/category/${video.category}` },
                { label: video.title },
              ]}
            />

            <article>
              {/* Header Image */}
              <div className="relative rounded-xl overflow-hidden mb-8 aspect-[21/9]">
                <img
                  src={article.image}
                  alt={article.title}
                  title={`${article.title} - 棒棒TV`}
                  className="w-full h-full object-cover"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-background-950/70 via-background-950/20 to-transparent"></div>
                <div className="absolute bottom-0 left-0 right-0 p-6 md:p-8">
                  <div className="flex flex-wrap items-center gap-2 mb-3">
                    {video.tags.map((tag) => (
                      <span key={tag} className="px-3 py-1 text-xs rounded-full bg-primary-500/20 text-primary-300 border border-primary-500/20 whitespace-nowrap">
                        {tag}
                      </span>
                    ))}
                  </div>
                  <h1 className="text-2xl md:text-3xl lg:text-4xl font-heading font-bold text-foreground-50 mb-2">
                    {article.title}
                  </h1>
                  <div className="flex flex-wrap items-center gap-4 text-sm text-foreground-400">
                    <span><i className="ri-user-line mr-1"></i>{article.author}</span>
                    <span><i className="ri-calendar-line mr-1"></i>{article.date}</span>
                    <span><i className="ri-eye-line mr-1"></i>{article.views.toLocaleString()} 阅读</span>
                    <span><i className="ri-heart-line mr-1"></i>{article.likes.toLocaleString()} 赞</span>
                  </div>
                </div>
              </div>

              {/* Video Info Card */}
              <aside className="bg-background-100 border border-background-300/30 rounded-xl p-5 mb-8">
                <div className="flex flex-col sm:flex-row gap-4 items-start">
                  <div className="flex-shrink-0 w-24 h-32 rounded-lg overflow-hidden">
                    <img
                      src={video.cover}
                      alt={video.title}
                      className="w-full h-full object-cover"
                    />
                  </div>
                  <div className="flex-1">
                    <h2 className="text-lg font-semibold text-foreground-100 mb-2">{video.title}</h2>
                    <div className="grid grid-cols-2 sm:grid-cols-3 gap-2 text-sm">
                      <div>
                        <span className="text-foreground-500">评分：</span>
                        <span className="text-accent-400 font-semibold">{video.rating}</span>
                      </div>
                      <div>
                        <span className="text-foreground-500">年份：</span>
                        <span className="text-foreground-200">{video.year}</span>
                      </div>
                      <div>
                        <span className="text-foreground-500">地区：</span>
                        <span className="text-foreground-200">{video.region}</span>
                      </div>
                      <div>
                        <span className="text-foreground-500">状态：</span>
                        <span className="text-foreground-200">{video.status}</span>
                      </div>
                      <div>
                        <span className="text-foreground-500">更新：</span>
                        <span className="text-foreground-200">{video.updateInfo}</span>
                      </div>
                      <div>
                        <span className="text-foreground-500">播放：</span>
                        <span className="text-foreground-200">{Math.floor(video.views / 10000)}万次</span>
                      </div>
                    </div>
                    {video.actors && video.actors.length > 0 && (
                      <div className="mt-3">
                        <span className="text-sm text-foreground-500">主演：</span>
                        <span className="text-sm text-foreground-200">{video.actors.join('、')}</span>
                      </div>
                    )}
                    {video.director && (
                      <div className="mt-1">
                        <span className="text-sm text-foreground-500">导演：</span>
                        <span className="text-sm text-foreground-200">{video.director}</span>
                      </div>
                    )}
                    <Link
                      to={`/category/${video.category}`}
                      className="whitespace-nowrap inline-flex items-center gap-1 mt-3 text-sm text-primary-400 hover:text-primary-300 transition-colors cursor-pointer"
                    >
                      查看更多{video.subCategory}
                      <i className="ri-arrow-right-line"></i>
                    </Link>
                  </div>
                </div>
              </aside>

              {/* Article Content */}
              <div className="prose prose-invert max-w-none mb-12">
                <div className="text-foreground-200 leading-relaxed text-base space-y-4">
                  {article.content.split('\n\n').map((paragraph, index) => {
                    if (paragraph.startsWith('## ')) {
                      return (
                        <h2 key={index} className="text-xl font-heading font-semibold text-foreground-100 mt-8 mb-4">
                          {paragraph.replace('## ', '')}
                        </h2>
                      );
                    }
                    return (
                      <p key={index} className="text-foreground-300 leading-loose">
                        {paragraph}
                      </p>
                    );
                  })}
                </div>
              </div>

              {/* Article Meta */}
              <div className="flex flex-wrap items-center gap-3 mb-12 pt-6 border-t border-background-300/30">
                <span className="text-sm text-foreground-500">标签：</span>
                {article.tags.map((tag) => (
                  <span key={tag} className="px-3 py-1 text-xs rounded-full bg-background-100 text-foreground-300 border border-background-300/30 whitespace-nowrap">
                    {tag}
                  </span>
                ))}
              </div>
            </article>

            {/* Related Videos */}
            {relatedVideos.length > 0 && (
              <section className="mb-12">
                <div className="flex items-center gap-2 mb-6">
                  <div className="w-1 h-5 bg-primary-500 rounded-full"></div>
                  <h2 className="text-lg font-heading font-semibold text-foreground-100">相关推荐</h2>
                </div>
                <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-6 gap-4 md:gap-5">
                  {relatedVideos.map((v) => (
                    <div key={v.id} className="animate-fade-in-up">
                      <VideoCard video={v} />
                    </div>
                  ))}
                </div>
              </section>
            )}
          </div>
        </main>
        <Footer />
      </div>
    </>
  );
}